import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../shared/Icon';
import '../Scenarios/Scenarios.css'; // scenario-row-remove

/*
 * Rows have no identity but their place, so focus after a removal goes to the
 * row's neighbour by index, or to the list's "+ Add" when none is left; a new
 * row's first field takes focus. `focusKey` names the input to focus once the
 * rows have re-rendered.
 */
function useRowFocus(rootRef) {
  const [target, setTarget] = useState(null);
  useEffect(() => {
    if (!target) return;
    rootRef.current?.querySelector(`[data-focus="${target}"]`)?.focus();
    setTarget(null);
  }, [target, rootRef]);
  return setTarget;
}

const RemoveButton = ({ label, icon = 'x', onClick }) => (
  <button type="button" className="scenario-row-remove" aria-label={label} onClick={onClick}>
    <Icon name={icon} size={20} />
  </button>
);

function PersonFields({ person, prefix, focusKey, onChange, onRemove, removeLabel }) {
  return (
    <div className="profile-row-fields">
      <input
        type="text"
        className="resui-input"
        placeholder={`${prefix} name`}
        aria-label={`${prefix} name`}
        data-focus={focusKey}
        value={person.name}
        onChange={(e) => onChange({ ...person, name: e.target.value })}
      />
      <input
        type="email"
        className="resui-input"
        placeholder="name@org.com"
        aria-label={`${prefix} email`}
        value={person.email}
        onChange={(e) => onChange({ ...person, email: e.target.value })}
      />
      <RemoveButton label={removeLabel} onClick={onRemove} />
    </div>
  );
}

const replace = (list, i, item) => list.map((x, j) => (j === i ? item : x));
const without = (list, i) => list.filter((_, j) => j !== i);

/** Teams: named groups, each a name and its teammates. */
export function TeamsRows({ teams, onChange }) {
  const rootRef = useRef(null);
  const focus = useRowFocus(rootRef);
  const setTeam = (ti, team) => onChange(replace(teams, ti, team));

  return (
    <div ref={rootRef}>
      <p className="navy-settings-desc">Named groups you share datasets, queries and automations with</p>
      {teams.map((team, ti) => (
        <div key={ti} className="profile-team">
          <div className="profile-row-fields">
            <input
              type="text"
              className="resui-input"
              placeholder="New team name"
              aria-label="Team name"
              data-focus={`team-${ti}`}
              value={team.name}
              onChange={(e) => setTeam(ti, { ...team, name: e.target.value })}
            />
            <RemoveButton
              label="Remove team"
              icon="trash"
              onClick={() => {
                onChange(without(teams, ti));
                focus(ti > 0 ? `team-${ti - 1}` : 'add-team');
              }}
            />
          </div>
          {team.members.map((member, mi) => (
            <PersonFields
                    key={mi}
              person={member}
              prefix="Teammate"
              focusKey={`team-${ti}-member-${mi}`}
              removeLabel="Remove teammate"
              onChange={(next) => setTeam(ti, { ...team, members: replace(team.members, mi, next) })}
              onRemove={() => {
                setTeam(ti, { ...team, members: without(team.members, mi) });
                focus(mi > 0 ? `team-${ti}-member-${mi - 1}` : `add-teammate-${ti}`);
              }}
            />
          ))}
          <div className="navy-settings-add">
            <button
              type="button"
              className="resui-link"
              data-focus={`add-teammate-${ti}`}
              onClick={() => {
                setTeam(ti, { ...team, members: [...team.members, { name: '', email: '' }] });
                focus(`team-${ti}-member-${team.members.length}`);
              }}
            >
              + Add teammate
            </button>
          </div>
        </div>
      ))}
      <div className="navy-settings-add">
        <button
          type="button"
          className="resui-link"
          data-focus="add-team"
          onClick={() => {
            onChange([...teams, { name: '', members: [{ name: '', email: '' }] }]);
            focus(`team-${teams.length}`);
          }}
        >
          + Add team
        </button>
      </div>
    </div>
  );
}

/** Reviewers: people who can review your work. */
export function ReviewersRows({ reviewers, onChange }) {
  const rootRef = useRef(null);
  const focus = useRowFocus(rootRef);
  return (
    <div ref={rootRef} className="profile-reviewers">
      <p className="navy-settings-desc">People who can review your work</p>
      {reviewers.map((reviewer, ri) => (
        <PersonFields
            key={ri}
          person={reviewer}
          prefix="Reviewer"
          focusKey={`reviewer-${ri}`}
          removeLabel="Remove reviewer"
          onChange={(next) => onChange(replace(reviewers, ri, next))}
          onRemove={() => {
            onChange(without(reviewers, ri));
            focus(ri > 0 ? `reviewer-${ri - 1}` : 'add-reviewer');
          }}
        />
      ))}
      <div className="navy-settings-add">
        <button
          type="button"
          className="resui-link"
          data-focus="add-reviewer"
          onClick={() => {
            onChange([...reviewers, { name: '', email: '' }]);
            focus(`reviewer-${reviewers.length}`);
          }}
        >
          + Add reviewer
        </button>
      </div>
    </div>
  );
}
